/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import Vue from "vue";
import { IWssEvent } from "./wss-event.interface";

/**
 * Known WebSocket event names from the backend.
 * Use these for type-safe subscriptions.
 * Note: Backend uses present tense (e.g., "message.create" not "message.created")
 */
export enum WssEventNameEnum {
  MESSAGE_CREATE = "message.create",
  CALL_CREATE = "call.create",
  REPORT_CREATE = "report.create",
  BOOKING_CREATE = "booking.create"
}

/**
 * Context passed to subscription handlers.
 * Provides access to the component and route information.
 */
export interface IWssSubscriptionContext {
  /**
   * The Vue component instance
   */
  component: Vue;

  /**
   * Route params from the component's $route
   */
  routeParams: Record<string, string>;

  /**
   * Function to manually unsubscribe this handler
   */
  unsubscribe: () => void;
}

/**
 * Type for filter functions that determine if an event should be handled
 */
export type WssSubscriptionFilter<T = Record<string, unknown>> = (
  event: IWssEvent<T>,
  context: IWssSubscriptionContext
) => boolean;

/**
 * Type for handler functions that react to events
 */
export type WssSubscriptionHandler<T = Record<string, unknown>> = (
  event: IWssEvent<T>,
  context: IWssSubscriptionContext
) => void | Promise<void>;

/**
 * Configuration for a single WebSocket event subscription.
 */
export interface IWssSubscriptionConfig<T = Record<string, unknown>> {
  /**
   * The event name to subscribe to (e.g., "message.created").
   * Can be a WssEventNameEnum value or any string for custom events.
   */
  event: WssEventNameEnum | string;

  /**
   * Handler function called when the event is received and passes the filter.
   */
  handler: WssSubscriptionHandler<T>;

  /**
   * Optional filter function to determine if the event should be handled.
   * Return true to handle the event, false to ignore it.
   * If not provided, all events of this type will be handled.
   */
  filter?: WssSubscriptionFilter<T>;

  /**
   * Optional debounce time in milliseconds.
   * If set, the handler will only be called after no events have been
   * received for the specified duration. Useful for batching rapid events.
   */
  debounce?: number;

  /**
   * Optional flag to only handle this event once, then automatically unsubscribe.
   */
  once?: boolean;
}

/**
 * Component-level configuration for WebSocket subscriptions.
 * Define this as a computed getter in your component.
 */
export interface IWssSubscriptions {
  /**
   * Array of subscription configurations
   */
  subscriptions: IWssSubscriptionConfig[];

  /**
   * If true, subscriptions are disabled (useful for conditional subscription)
   */
  disabled?: boolean;
}
